// src/pages/ProposalList.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import axios from 'axios';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  AdjustmentsHorizontalIcon,
  FunnelIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import ProposalCard from '../components/ProposalCard';

const ProposalList = () => {
  const { account, isOasysNetwork } = useWeb3();

  const [proposals, setProposals] = useState([]);
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pairFilter, setPairFilter] = useState('all');
  // フィルターは最初から表示
  const [showFilters, setShowFilters] = useState(true);
  // Active token pairs from ContractAddress
  const [pairTypes, setPairTypes] = useState(['WOAS', 'MCHC', 'MSM', 'Z']);

  // 提案一覧を取得
  useEffect(() => {
    const fetchProposals = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get proposals
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/proposals`);
        // tokenがnullでない、かつtokenのgrantTokenもnullでない提案のみをフィルタリング
        const validProposals = response.data.filter(
          proposal => proposal.token && proposal.token.grantToken
        );
        setProposals(validProposals);

        // 初期表示では全ての提案を表示
        setFilteredProposals(validProposals);

        // Get active token pairs from ContractAddress
        try {
          const contractResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/contracts/active`
          );

          // デフォルトのペア（常に表示）
          const defaultPairs = ['WOAS', 'MCHC', 'MSM', 'Z'];

          // バックエンドからのアクティブなペアを取得
          const activePairs = contractResponse.data
            .sort((a, b) => a.id - b.id)
            .map(pair => pair.name);

          // 必要なペアがすべて含まれるようにする
          const missingPairs = defaultPairs.filter(pair => !activePairs.includes(pair));

          // 必要なペアをすべて含む最終リスト
          const allPairs = [...activePairs];

          // 不足しているペアを追加
          missingPairs.forEach(pair => {
            if (!allPairs.includes(pair)) {
              allPairs.push(pair);
            }
          });

          setPairTypes(allPairs);
        } catch (pairError) {
          console.error('トークンペア取得エラー:', pairError);
          // エラー時はデフォルトのペアを使用
          setPairTypes(['WOAS', 'MCHC', 'MSM', 'Z']);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('提案取得エラー:', error);
        setError('提案一覧の取得に失敗しました');
        setIsLoading(false);
      }
    };

    fetchProposals();
  }, []);

  // 検索とフィルタの適用
  useEffect(() => {
    // 現在の日時
    const now = new Date();

    // 検索とステータスフィルタを適用
    let filtered = [...proposals];

    // 検索フィルタ
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        proposal =>
          proposal.title.toLowerCase().includes(query) ||
          proposal.description.toLowerCase().includes(query) ||
          proposal.token?.symbol.toLowerCase().includes(query)
      );
    }

    // ステータスフィルタ
    if (statusFilter !== 'all') {
      filtered = filtered.filter(proposal => {
        const startTime = new Date(proposal.startTime);
        const endTime = new Date(proposal.endTime);

        switch (statusFilter) {
          case 'active':
            return now >= startTime && now <= endTime;
          case 'pending':
            return now < startTime;
          case 'closed':
            return now > endTime;
          default:
            return true;
        }
      });
    }

    // ペアタイプフィルタ
    if (pairFilter !== 'all') {
      filtered = filtered.filter(proposal => proposal.token?.pairType === pairFilter);
    }

    setFilteredProposals(filtered);
  }, [proposals, searchQuery, statusFilter, pairFilter]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">提案一覧</h1>

        {account && isOasysNetwork && (
          <Link
            to="/proposals/create"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            新しい提案
          </Link>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* 検索とフィルター */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="タイトル、説明、シンボルで検索..."
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="ml-2 p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-300 dark:border-gray-600 rounded"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
          </button>
        </div>

        {/* フィルターオプション - デフォルトで表示 */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-4">
              {/* ステータスフィルター - 進行順にリスト変更 */}
              <div>
                <div className="flex items-center mb-2">
                  <FunnelIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">ステータス:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setStatusFilter('all')}
                    className={`px-3 py-1.5 text-sm rounded-full ${
                      statusFilter === 'all'
                        ? 'bg-blue-600 dark:bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    すべて
                  </button>

                  <button
                    onClick={() => setStatusFilter('pending')}
                    className={`px-3 py-1.5 text-sm rounded-full ${
                      statusFilter === 'pending'
                        ? 'bg-yellow-600 dark:bg-yellow-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    準備中
                  </button>

                  <button
                    onClick={() => setStatusFilter('active')}
                    className={`px-3 py-1.5 text-sm rounded-full ${
                      statusFilter === 'active'
                        ? 'bg-green-600 dark:bg-green-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    進行中
                  </button>

                  <button
                    onClick={() => setStatusFilter('closed')}
                    className={`px-3 py-1.5 text-sm rounded-full ${
                      statusFilter === 'closed'
                        ? 'bg-gray-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    終了
                  </button>
                </div>
              </div>

              {/* ペアタイプフィルター */}
              <div>
                <div className="flex items-center mb-2">
                  <TagIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">トークンペア:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setPairFilter('all')}
                    className={`px-3 py-1.5 text-sm rounded-full ${
                      pairFilter === 'all'
                        ? 'bg-blue-600 dark:bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    すべて
                  </button>

                  {/* デフォルトの4ペアタイプを常に表示 */}
                  {pairTypes.map(pairType => (
                    <button
                      key={pairType}
                      onClick={() => setPairFilter(pairType)}
                      className={`px-3 py-1.5 text-sm rounded-full ${
                        pairFilter === pairType
                          ? 'bg-blue-600 dark:bg-blue-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {pairType}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {filteredProposals.length} 件の提案が見つかりました
          {(searchQuery || statusFilter !== 'all' || pairFilter !== 'all') && (
            <span> (フィルター適用中)</span>
          )}
        </div>
      </div>

      {/* 提案リスト */}
      {filteredProposals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProposals.map(proposal => (
            <ProposalCard key={proposal.id} proposal={proposal} />
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <div className="text-gray-500 dark:text-gray-400 mb-4">提案が見つかりませんでした</div>

          {(searchQuery || statusFilter !== 'all' || pairFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setPairFilter('all');
              }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              フィルターをクリア
            </button>
          )}

          {account && isOasysNetwork && (
            <div className="mt-4">
              <Link
                to="/proposals/create"
                className="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition"
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                新しい提案を作成
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProposalList;
